/* Native storefront forms -> Zendesk ticket proxy.
   Conditional visibility engine + file uploads + submission.
   Markup contract (generated snippets):
   - .zf-field/.zf-row wrappers with data-el-id
   - data-showif='{"rules":[{"f":elId,"v":value}],"or":bool,"mode":"show"|"hide"}'
   - inputs carry name=<elId>, data-label, optional data-zf-name / data-zf-email
   - <script type="application/json" data-zf-meta> carries {title,description,success,proxyKey}
*/
(function () {
  'use strict';

  function init(form) {
    if (form.dataset.zfReady) return;
    form.dataset.zfReady = '1';

    var wrap = form.closest('.zf-wrap');
    var metaEl = form.querySelector('[data-zf-meta]');
    var meta = {};
    try { meta = JSON.parse(metaEl ? metaEl.textContent : '{}'); } catch (e) { /* noop */ }

    var dt = form.querySelector('[data-zf-default-title]');
    if (dt && meta.title) dt.textContent = meta.title;
    var dd = form.querySelector('[data-zf-default-desc]');
    if (dd) { if (meta.description) dd.textContent = meta.description; else dd.remove(); }

    // ---- conditional engine ----------------------------------------------
    // Hidden fields are disabled and keep whatever was picked before they were hidden, so a
    // disabled input counts as empty: switching Wholesaler -> Official must not leave the
    // wholesaler's follow-up questions showing.
    function valueOf(elId) {
      var inputs = form.querySelectorAll('[name="' + (window.CSS && CSS.escape ? CSS.escape(elId) : elId) + '"]');
      if (!inputs.length) return [];
      var first = inputs[0];
      if (first.type === 'radio') {
        var r = form.querySelector('[name="' + elId + '"]:checked:not(:disabled)');
        return r ? [r.value] : [];
      }
      if (first.type === 'checkbox') {
        return Array.prototype.map.call(
          form.querySelectorAll('[name="' + elId + '"]:checked:not(:disabled)'), function (c) { return c.value; });
      }
      return first.value && !first.disabled ? [first.value] : [];
    }

    var condEls = Array.prototype.slice.call(form.querySelectorAll('[data-showif]'));
    function applyConditions() {
      var changed = false;
      condEls.forEach(function (el) {
        var cfg;
        try { cfg = JSON.parse(el.getAttribute('data-showif')); } catch (e) { return; }
        var results = (cfg.rules || []).map(function (r) {
          return valueOf(r.f).indexOf(r.v) !== -1;
        });
        var matched = cfg.or ? results.some(Boolean) : results.every(Boolean);
        var visible = cfg.mode === 'hide' ? !matched : matched;
        if (el.hidden === visible) { el.hidden = !visible; changed = true; }
        // hidden fields must not validate or submit
        el.querySelectorAll('input, select, textarea').forEach(function (i) { i.disabled = !visible; });
      });
      if (changed) applyConditions(); // cascade (conditions on conditional fields)
    }
    form.addEventListener('change', applyConditions);
    form.addEventListener('input', function (e) {
      if (e.target && (e.target.tagName === 'SELECT' || e.target.type === 'radio' || e.target.type === 'checkbox')) applyConditions();
    });
    applyConditions();

    // ---- file checks: tell the customer when they pick the file, not after the whole form ----
    // Up to 50MB attaches to the ticket (Zendesk's per-attachment limit). A bigger video goes
    // straight to the proxy's storage bucket and the ticket gets a link to it.
    var MAX_FILE = 50 * 1024 * 1024;
    var MAX_VIDEO = 500 * 1024 * 1024;
    var VIDEO_EXT = /\.(mp4|mov|m4v|webm|3gp|3g2|avi|mkv|mts)$/i;
    function isVideo(f) { return /^video\//.test(f.type || '') || VIDEO_EXT.test(f.name || ''); }
    function videoType(f) {
      if (/^video\//.test(f.type || '')) return f.type;
      var ext = (f.name.match(VIDEO_EXT) || [])[1] || 'mp4';
      ext = ext.toLowerCase();
      return 'video/' + (ext === 'mov' ? 'quicktime' : ext === 'mkv' ? 'x-matroska' : ext === 'avi' ? 'x-msvideo' : ext);
    }
    function fileProblem(input, f) {
      if (input.hasAttribute('data-zf-video') && !isVideo(f)) {
        return '"' + f.name + '" ' + (input.getAttribute('data-zf-notvideo') || 'isn’t a video. Please choose a video file.');
      }
      if (isVideo(f) ? f.size > MAX_VIDEO : f.size > MAX_FILE) {
        return '"' + f.name + '" ' + (input.getAttribute('data-zf-toobig') ||
          (isVideo(f) ? 'is over 500MB, so it can’t be uploaded. Please choose a shorter video.'
                      : 'is over 50MB, so it can’t be attached. Please choose a smaller file.'));
      }
      return '';
    }
    form.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'file') return;
      var wrapEl = input.closest('.zf-field') || input.parentNode;
      var note = wrapEl.querySelector('.zf-fileerr');
      var problem = '';
      Array.prototype.some.call(input.files || [], function (f) { problem = fileProblem(input, f); return !!problem; });
      if (!problem) { if (note) note.remove(); return; }
      if (!note) {
        note = document.createElement('p');
        note.className = 'zf-fileerr';
        note.setAttribute('role', 'alert');
        input.insertAdjacentElement('afterend', note);
      }
      note.textContent = problem;
      input.value = '';
    });

    // ---- order check: does the order number match the email, and is a return still in window? ----
    // Asks the proxy, which asks BeccaBot's Shopify mirror. Informational only: it never blocks
    // submitting, and says nothing at all when the check is unavailable.
    var ocNum = form.querySelector('[data-zf-ordercheck="number"]');
    var ocMail = form.querySelector('[data-zf-ordercheck="email"]');
    var ocOut = form.querySelector('.zf-orderstatus');
    var ocType = form.querySelector('[name="select-1"]');
    var ocLastKey = '', ocResult = null;
    function ocRender() {
      if (!ocOut) return;
      var d = ocResult, type = ocType ? ocType.value : '';
      var html = '', cls = '';
      if (d && d.status === 'no_match') {
        cls = 'zf-orderstatus--warn';
        html = 'We couldn’t match this order number to this email. Please double-check both — they’re in your order confirmation email. You can still submit, and our team will look it up.';
      } else if (d && d.status === 'match' && d.window === 'closed' && (!type || type === 'Return Request')) {
        cls = 'zf-orderstatus--warn';
        html = 'We found your order. It was delivered ' + d.days_since_delivery + ' days ago, and returns are accepted within 60 days of delivery. ' +
          'If your product isn’t working, it may be covered by our warranty: <a href="/pages/warranty-review">submit a Warranty Claim Review</a>. You can still submit this request.';
      } else if (d && d.status === 'match') {
        cls = 'zf-orderstatus--ok';
        html = '✓ We found your order.';
      }
      ocOut.className = 'zf-orderstatus ' + cls;
      ocOut.innerHTML = html;
      ocOut.hidden = !html;
    }
    function ocCheck() {
      if (!ocNum || !ocMail || !ocOut) return;
      var n = ocNum.value.trim(), m = ocMail.value.trim();
      if (!n || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(m)) return;
      var key = n.toUpperCase() + '|' + m.toLowerCase();
      if (key === ocLastKey) return;
      ocLastKey = key;
      fetch(form.getAttribute('data-zf-proxy') + '/order-check', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: n, email: m })
      }).then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) { if (key === ocLastKey) { ocResult = d; ocRender(); } })
        .catch(function () { /* unavailable: say nothing */ });
    }
    if (ocNum && ocMail) {
      ocNum.addEventListener('change', ocCheck);
      ocMail.addEventListener('change', ocCheck);
      ocNum.addEventListener('input', function () { if (ocResult) { ocResult = null; ocLastKey = ''; ocRender(); } });
      if (ocType) ocType.addEventListener('change', ocRender);
    }

    // ---- submit -----------------------------------------------------------
    var btn = form.querySelector('.zf-submit');
    var errBox = form.querySelector('.zf-error');
    var progress = form.querySelector('.zf-progress');
    var SUPPORT = 'support@echowater.com';

    function fail(msg) {
      errBox.textContent = msg;
      errBox.style.display = 'block';
      btn.disabled = false;
      btn.textContent = btn.dataset.zfLabel || 'Submit';
      if (progress) progress.style.display = 'none';
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      errBox.style.display = 'none';

      // native validation on enabled fields; checkbox-groups: require >=1 when flagged
      if (!form.reportValidity()) return;
      var groupFail = null;
      var seen = {};
      form.querySelectorAll('input[data-zf-groupreq]:not(:disabled)').forEach(function (c) {
        if (seen[c.name]) return; seen[c.name] = 1;
        var wrapEl = c.closest('.zf-field');
        if (wrapEl && wrapEl.hidden) return;
        if (!form.querySelector('input[name="' + c.name + '"]:checked')) {
          groupFail = wrapEl || c;
        }
      });
      if (groupFail) {
        groupFail.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return fail('Please select at least one option in the highlighted section.');
      }
      // either/or: at least one enabled input sharing a data-zf-oneof name must be filled
      // (e.g. upload a video OR paste a link to it)
      var oneof = {};
      form.querySelectorAll('[data-zf-oneof]:not(:disabled)').forEach(function (i) {
        var g = i.getAttribute('data-zf-oneof');
        var filled = i.type === 'file' ? !!(i.files && i.files.length) : !!(i.value && i.value.trim());
        if (!oneof[g]) oneof[g] = { filled: false, el: i };
        if (filled) oneof[g].filled = true;
        if (i.getAttribute('data-zf-oneof-msg')) oneof[g].el = i;
      });
      var missing = Object.keys(oneof).map(function (g) { return oneof[g]; }).filter(function (o) { return !o.filled; })[0];
      if (missing) {
        (missing.el.closest('.zf-field') || missing.el).scrollIntoView({ behavior: 'smooth', block: 'center' });
        return fail(missing.el.getAttribute('data-zf-oneof-msg') || 'Please fill in one of the highlighted fields.');
      }

      var proxy = form.getAttribute('data-zf-proxy');
      btn.dataset.zfLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Sending…';

      var name = '', email = '', fields = {}, files = [];
      form.querySelectorAll('input, select, textarea').forEach(function (i) {
        if (i.disabled || i.name === 'website' || !i.name) return;
        var label = i.getAttribute('data-label') || i.name;
        if (i.type === 'file') {
          Array.prototype.forEach.call(i.files || [], function (f) { files.push({ file: f, input: i }); });
          return;
        }
        if (i.type === 'radio' || i.type === 'checkbox') {
          if (!i.checked) return;
          fields[label] = fields[label] ? fields[label] + '; ' + i.value : i.value;
          return;
        }
        if (!i.value) return;
        if (i.hasAttribute('data-zf-email') && !email) { email = i.value; }
        if (i.hasAttribute('data-zf-name')) { name = name ? name + ' ' + i.value : i.value; }
        fields[label] = i.value;
      });
      // fallbacks: first text field named like a name; email from any email input
      if (!email) { var e0 = form.querySelector('input[type="email"]:not(:disabled)'); if (e0) email = e0.value; }
      if (!name) {
        var n0 = form.querySelector('input[data-zf-name]:not(:disabled)') ||
                 form.querySelector('input[type="text"]:not(:disabled)');
        if (n0) name = n0.value;
      }

      // which page the form was sent from, and on a product page which product (the Globo forms said so)
      fields['Page URL'] = location.origin + location.pathname;
      if (/\/products\//.test(location.pathname) && !fields['Product']) {
        var og = document.querySelector('meta[property="og:title"]');
        var productTitle = (og && og.getAttribute('content')) || document.title;
        if (productTitle) fields['Product'] = productTitle;
      }

      var problem = '';
      files.some(function (x) { problem = fileProblem(x.input, x.file); return !!problem; });
      if (problem) return fail(problem);

      // A video over 50MB: the proxy presigns a PUT to its bucket, and the browser sends the file
      // there directly. Resolves to the ref the proxy turns into a link on the ticket.
      function uploadLarge(f, label) {
        return fetch(proxy + '/video-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: f.name, size: f.size, type: videoType(f) })
        }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, status: r.status, d: d }; }); })
          .then(function (res) {
            if (!res.ok || !res.d.url || !res.d.ref) {
              var err = new Error('video upload unavailable');
              err.zf = res.status === 429 ? 'rate' : (res.status === 404 || res.status === 503) ? 'large' : 'upload';
              err.file = f.name;
              throw err;
            }
            return new Promise(function (resolve, reject) {
              var xhr = new XMLHttpRequest();
              xhr.open('PUT', res.d.url);
              xhr.setRequestHeader('Content-Type', videoType(f));
              xhr.upload.onprogress = function (e) {
                if (progress && e.lengthComputable) progress.textContent = label + ' ' + Math.floor(e.loaded / e.total * 100) + '%';
              };
              xhr.onload = function () {
                if (xhr.status >= 200 && xhr.status < 300) return resolve(res.d.ref);
                var err = new Error('video upload failed: HTTP ' + xhr.status);
                err.zf = 'upload'; err.file = f.name;
                reject(err);
              };
              xhr.onerror = function () {
                var err = new Error('video upload failed');
                err.zf = 'upload'; err.file = f.name;
                reject(err);
              };
              xhr.send(f);
            });
          });
      }

      // 1) upload files: up to 50MB through the proxy -> zendesk upload tokens; bigger videos -> bucket refs
      var uploadTokens = [];
      var videoRefs = [];
      var chain = Promise.resolve();
      files.forEach(function (x, idx) {
        var f = x.file;
        chain = chain.then(function () {
          var label = 'Uploading file ' + (idx + 1) + ' of ' + files.length + '…';
          if (progress) {
            progress.style.display = 'block';
            progress.textContent = label;
          }
          if (isVideo(f) && f.size > MAX_FILE) {
            return uploadLarge(f, label).then(function (ref) { videoRefs.push(ref); }, function (e) {
              if (!e.zf) { e.zf = 'upload'; e.file = f.name; }
              throw e;
            });
          }
          return fetch(proxy + '/upload?filename=' + encodeURIComponent(f.name), {
            method: 'POST',
            headers: { 'Content-Type': f.type || 'application/octet-stream' },
            body: f
          }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, status: r.status, d: d }; }); })
            .then(function (res) {
              if (!res.ok || !res.d.token) {
                var err = new Error('upload failed');
                err.zf = res.status === 429 ? 'rate' : 'upload';
                err.file = f.name;
                throw err;
              }
              uploadTokens.push(res.d.token);
            }, function (e) {
              if (!e.zf) { e.zf = 'upload'; e.file = f.name; }
              throw e;
            });
        });
      });

      // 2) submit the ticket
      chain.then(function () {
        if (progress) { progress.textContent = 'Submitting…'; }
        return fetch(proxy + '/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form: meta.proxyKey || 'contact',
            name: name, email: email,
            fields: fields,
            uploads: uploadTokens,
            videos: videoRefs,
            website: form.querySelector('[name="website"]').value || undefined
          })
        });
      }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, status: r.status, d: d }; }); })
        .then(function (res) {
          if (!res.ok || !res.d.ok) {
            var err = new Error((res.d && res.d.error) || 'submit failed');
            if (res.status === 429) err.zf = 'rate';
            throw err;
          }
          form.style.display = 'none';
          var s = wrap.querySelector('.zf-success');
          var sm = s.querySelector('[data-zf-success-msg]');
          if (sm) sm.textContent = meta.success || 'We received your submission and will get back to you soon.';
          s.style.display = 'block';
          s.scrollIntoView({ behavior: 'smooth', block: 'center' });
        })
        .catch(function (e) {
          console.error('zendesk form error', e);
          if (e && e.zf === 'rate') {
            return fail('We’ve received several attempts from your connection. Please wait a few minutes and try again, or email ' + SUPPORT + '.');
          }
          if (e && e.zf === 'large') {
            return fail('"' + e.file + '" is over 50MB and couldn’t be uploaded. Please email it to ' + SUPPORT + '. Everything else you entered is still here.');
          }
          if (e && e.zf === 'upload') {
            return fail('"' + e.file + '" didn’t upload. Please try again — if it keeps failing, try a smaller file or email it to ' + SUPPORT + '. Everything else you entered is still here.');
          }
          fail('Something went wrong sending your request. Please try again, or email ' + SUPPORT + ' and we’ll help.');
        });
    });
  }

  function boot() { document.querySelectorAll('.zf-form').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot); // theme editor re-renders
})();
